import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiArrowLeft, FiTrendingUp, FiTrendingDown, FiDollarSign, FiCheckCircle, FiUsers, FiChevronLeft, FiChevronRight, FiLink, FiShield } from 'react-icons/fi';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { Loader } from '../components/Common/Loader';
import { ErrorDisplay } from '../components/Common/ErrorDisplay';
import { adminApi } from '../api';
import { useAuthStore } from '../stores';
import { Trade } from '../types';

const StatisticsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const userId = searchParams.get('userid');
  const [activeTab, setActiveTab] = useState<'followers' | 'trades'>('followers');
  const [followersPage, setFollowersPage] = useState(1);
  const [tradesPage, setTradesPage] = useState(1);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const itemsPerPage = 10;

  const { data: statisticsData, isLoading: isLoadingStats, error: statsError } = useQuery({
    queryKey: ['admin', 'user-statistics', userId],
    queryFn: () => adminApi.getUserStatistics(userId!),
    enabled: !!userId && (user?.role === 'admin' || user?.role === 'master'),
  });

  // Automatically switch to trades tab if the user is not a master
  React.useEffect(() => {
    if (statisticsData?.statistics?.user?.role && statisticsData.statistics.user.role !== 'master') {
      setActiveTab('trades');
    }
  }, [statisticsData]);

  const { data: followersData, isLoading: isLoadingFollowers } = useQuery({
    queryKey: ['admin', 'user-followers', userId, followersPage],
    queryFn: () => adminApi.getUserFollowers(userId!, followersPage, itemsPerPage),
    enabled: !!userId && user?.role === 'admin' && activeTab === 'followers',
  });

  const { data: tradesData, isLoading: isLoadingTrades } = useQuery({
    queryKey: ['admin', 'user-trades', userId, tradesPage],
    queryFn: () => adminApi.getUserTrades(userId!, tradesPage, itemsPerPage),
    enabled: !!userId && (user?.role === 'admin' || user?.role === 'master') && activeTab === 'trades',
  });

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-background">
        {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
        <div className="flex relative pt-16 lg:pt-0">
          <Sidebar
            activeTab="overview"
            setActiveTab={() => { }}
            user={user!}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <main className="flex-1 px-4 py-4 sm:px-6 lg:px-6 lg:ml-64 overflow-x-hidden">
            <ErrorDisplay error="User ID is required" />
          </main>
        </div>
      </div>
    );
  }

  if (user?.role !== 'admin' && user?.role !== 'master') {
    return (
      <div className="min-h-screen bg-background">
        {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
        <div className="flex relative pt-16 lg:pt-0">
          <Sidebar
            activeTab="overview"
            setActiveTab={() => { }}
            user={user!}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <main className="flex-1 px-4 py-4 sm:px-6 lg:px-6 lg:ml-64 overflow-x-hidden">
            <ErrorDisplay error="Access denied" />
          </main>
        </div>
      </div>
    );
  }

  if (isLoadingStats) {
    return (
      <div className="min-h-screen bg-background">
        {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
        <div className="flex relative pt-16 lg:pt-0">
          <Sidebar
            activeTab="overview"
            setActiveTab={() => { }}
            user={user!}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <main className="flex-1 px-4 py-4 sm:px-6 lg:px-6 lg:ml-64 overflow-x-hidden">
            <Loader message="Loading statistics..." />
          </main>
        </div>
      </div>
    );
  }

  if (statsError || !statisticsData?.statistics) {
    return (
      <div className="min-h-screen bg-background">
        {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
        <div className="flex relative pt-16 lg:pt-0">
          <Sidebar
            activeTab="overview"
            setActiveTab={() => { }}
            user={user!}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <main className="flex-1 px-4 py-4 sm:px-6 lg:px-6 lg:ml-64 overflow-x-hidden">
            <ErrorDisplay error="Failed to load statistics" />
          </main>
        </div>
      </div>
    );
  }

  const { user: targetUser, tradeStats, brokerageConnections } = statisticsData.statistics;
  const isMaster = targetUser.role === 'master';

  return (
    <div className="min-h-screen bg-background">
      {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
      <div className="flex relative pt-16 lg:pt-0">
        <Sidebar
          activeTab="overview"
          setActiveTab={() => { }}
          user={user!}
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 px-4 py-4 sm:px-6 lg:px-6 lg:ml-64 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-muted hover:text-foreground transition-colors mb-4"
              >
                <FiArrowLeft className="h-5 w-5" />
                <span>Back</span>
              </button>
              <h1 className="text-4xl title-page">
                User Statistics
              </h1>
              <p className="text-muted mt-2">Trade performance and statistics</p>
            </div>

            {/* User Info Card */}
            <div className="card mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{targetUser.name}</h2>
                  {/* <p className="text-muted mt-1">{targetUser.email}</p> */}
                  <div className="flex items-center gap-4 mt-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${targetUser.role === 'master'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                      {targetUser.role.charAt(0).toUpperCase() + targetUser.role.slice(1)}
                    </span>
                    <span className="text-muted">
                      Balance: <span className="font-semibold text-foreground">${targetUser.accountBalance.toLocaleString()}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trade Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4 mb-6">


              {/* Today P&L */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${(tradeStats.todayPnL || 0) >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {(tradeStats.todayPnL || 0) >= 0 ? (
                      <FiTrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                    ) : (
                      <FiTrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted">Today P&L</p>
                    <p className={`text-2xl font-bold ${(tradeStats.todayPnL ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ${(tradeStats.todayPnL ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Weekly P&L */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${(tradeStats.weeklyPnL || 0) >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {(tradeStats.weeklyPnL || 0) >= 0 ? (
                      <FiTrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                    ) : (
                      <FiTrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted">Weekly P&L</p>
                    <p className={`text-2xl font-bold ${(tradeStats.weeklyPnL ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ${(tradeStats.weeklyPnL ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Monthly P&L */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${(tradeStats.monthlyPnL || 0) >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {(tradeStats.monthlyPnL || 0) >= 0 ? (
                      <FiTrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                    ) : (
                      <FiTrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted">Monthly P&L</p>
                    <p className={`text-2xl font-bold ${(tradeStats.monthlyPnL ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ${(tradeStats.monthlyPnL ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total P&L */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${(tradeStats.totalProfitLoss ?? 0) >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {(tradeStats.totalProfitLoss ?? 0) >= 0 ? (
                      <FiTrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                    ) : (
                      <FiTrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted">Total P&L</p>
                    <p className={`text-2xl font-bold ${(tradeStats.totalProfitLoss ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ${(tradeStats.totalProfitLoss ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total Trades */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiTrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted">Total Trades</p>
                    <p className="text-2xl font-bold text-foreground">{tradeStats.totalTrades}</p>
                  </div>
                </div>
              </div>

              {/* Successful Trades */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FiCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted">Successful Trades</p>
                    <p className="text-2xl font-bold text-foreground">{tradeStats.successfulTrades}</p>
                  </div>
                </div>
              </div>

              {/* Win Rate */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FiDollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted">Win Rate</p>
                    <p className="text-2xl font-bold text-foreground">{tradeStats.winRate.toFixed(2)}%</p>
                  </div>
                </div>
              </div>

              {/* Connected Accounts */}
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <FiLink className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted">Connected Accounts</p>
                    <p className="text-2xl font-bold text-foreground">{brokerageConnections?.length || 0}</p>
                    {brokerageConnections?.length > 0 && (
                      <p className="text-xs text-muted mt-1">
                        Total: ${brokerageConnections.reduce((sum, conn) => sum + (conn.accountBalance || 0), 0).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

            </div>


            {/* Detailed Statistics */}
            <div className="card mb-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Trade Performance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted">Profitable Trades</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">{tradeStats.profitableTrades}</p>
                </div>
                <div>
                  <p className="text-sm text-muted">Losing Trades</p>
                  <p className="text-lg font-semibold text-red-600 dark:text-red-400">{tradeStats.losingTrades}</p>
                </div>
                <div>
                  <p className="text-sm text-muted">Failed Trades</p>
                  <p className="text-lg font-semibold text-muted">{tradeStats.failedTrades}</p>
                </div>
                <div>
                  <p className="text-sm text-muted">Success Rate</p>
                  <p className="text-lg font-semibold text-foreground">
                    {tradeStats.totalTrades > 0
                      ? ((tradeStats.successfulTrades / tradeStats.totalTrades) * 100).toFixed(2)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>

            {/* Brokerage Connections */}
            <div className="card mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FiLink className="h-5 w-5 text-primary-600" />
                <h3 className="text-xl font-bold text-foreground">Connected Accounts</h3>
                <span className="text-sm text-muted">
                  ({brokerageConnections?.length || 0} {brokerageConnections?.length === 1 ? 'account' : 'accounts'})
                </span>
              </div>
              {brokerageConnections && brokerageConnections.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Brokerage</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Account Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Account Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Connection Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Balance</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Buying Power</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Primary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brokerageConnections.map((connection) => (
                        <tr key={connection._id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-foreground">{connection.brokerageName}</span>
                              {connection.isPrimary && (
                                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded text-xs font-semibold">
                                  Primary
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-foreground">{connection.accountName || 'N/A'}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${connection.accountType === 'live'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              }`}>
                              {connection.accountType === 'live' ? 'Live' : connection.accountType === 'paper' ? 'Paper' : connection.accountType.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-muted">
                              {connection.connectionType === 'snaptrade' ? 'SnapTrade' :
                                connection.connectionType === 'direct_ibkr' ? 'Direct IBKR' :
                                  connection.connectionType}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-foreground">
                              {connection.currency || 'USD'} {connection.accountBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-foreground">
                              {connection.currency || 'USD'} {connection.buyingPower.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${connection.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : connection.status === 'error' || connection.status === 'disconnected'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}>
                              {connection.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {connection.isPrimary ? (
                              <FiShield className="h-5 w-5 text-primary-600" title="Primary Account" />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted">
                  <FiLink className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">No accounts connected</p>
                  <p className="text-sm mt-2">This user hasn't connected any brokerage accounts yet.</p>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="card mb-6">
              <div className="flex items-center gap-4 border-b border-border mb-4">
                {targetUser.role === 'master' && (
                  <button
                    onClick={() => {
                      setActiveTab('followers');
                      setFollowersPage(1);
                    }}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${activeTab === 'followers'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-muted hover:text-foreground'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <FiUsers className="h-4 w-4" />
                      <span>Followers</span>
                      {isMaster && followersData?.pagination && (
                        <span className="text-xs bg-surface-elevated px-2 py-0.5 rounded-full">
                          {followersData.pagination.total}
                        </span>
                      )}
                    </div>
                  </button>
                )}
                <button
                  onClick={() => {
                    setActiveTab('trades');
                    setTradesPage(1);
                  }}
                  className={`px-4 py-2 font-semibold transition-colors border-b-2 ${activeTab === 'trades'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-muted hover:text-foreground'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <FiTrendingUp className="h-4 w-4" />
                    <span>Trades</span>
                    {tradesData?.pagination && (
                      <span className="text-xs bg-surface-elevated px-2 py-0.5 rounded-full">
                        {tradesData.pagination.total}
                      </span>
                    )}
                  </div>
                </button>
              </div>

              {/* Followers Tab Content */}
              {activeTab === 'followers' && (
                <div>
                  {isLoadingFollowers ? (
                    <Loader message="Loading followers..." />
                  ) : isMaster && followersData?.followers && followersData.followers.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Follower</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Total Trades</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Successful</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Total P&L</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Win Rate</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Allocation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {followersData.followers.map((followerData) => (
                              <tr key={followerData.follower._id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="font-semibold text-foreground">{followerData.follower.name}</p>
                                    <p className="text-sm text-muted">{followerData.follower.email}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-foreground font-medium">{followerData.stats.totalTrades}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-green-600 dark:text-green-400 font-medium">
                                    {followerData.stats.successfulTrades}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`font-semibold ${followerData.stats.totalProfitLoss >= 0
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-600 dark:text-red-400'
                                    }`}>
                                    ${followerData.stats.totalProfitLoss.toLocaleString()}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-foreground font-medium">
                                    {followerData.stats.winRate.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span
                                    className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${
                                      followerData.relationship?.status === 'paused'
                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                        : followerData.relationship?.status === 'pending'
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                        : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                    }`}
                                  >
                                    {followerData.relationship?.status === 'paused'
                                      ? 'Paused'
                                      : followerData.relationship?.status === 'pending'
                                      ? 'Pending'
                                      : 'Active'}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-foreground font-medium">
                                    {followerData.relationship.allocationPercent}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination for Followers */}
                      {followersData.pagination && followersData.pagination.pages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <div className="text-sm text-muted">
                            Showing {(followersPage - 1) * itemsPerPage + 1} to {Math.min(followersPage * itemsPerPage, followersData.pagination.total)} of {followersData.pagination.total} followers
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setFollowersPage(p => Math.max(1, p - 1))}
                              disabled={followersPage === 1}
                              className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiChevronLeft className="h-5 w-5" />
                            </button>
                            <span className="px-4 py-2 text-sm font-medium text-foreground">
                              Page {followersPage} of {followersData.pagination.pages}
                            </span>
                            <button
                              onClick={() => setFollowersPage(p => Math.min(followersData.pagination.pages, p + 1))}
                              disabled={followersPage === followersData.pagination.pages}
                              className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiChevronRight className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : isMaster ? (
                    <div className="text-center py-8 text-muted">
                      <FiUsers className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium">No active followers</p>
                      <p className="text-sm mt-2">This master trader doesn't have any active followers yet.</p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted">
                      <p className="text-lg font-medium">Not a master trader</p>
                      <p className="text-sm mt-2">This user is not a master trader, so there are no followers to display.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Trades Tab Content */}
              {activeTab === 'trades' && (
                <div>
                  {isLoadingTrades ? (
                    <Loader message="Loading trades..." />
                  ) : tradesData?.trades && tradesData.trades.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Symbol</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Side</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Quantity</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Price</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>

                              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tradesData.trades.map((trade: Trade) => (
                              <tr key={trade._id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                                <td className="py-3 px-4">
                                  <span className="font-semibold text-foreground">{trade.symbol}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${trade.side === 'buy'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                    {trade.side.toUpperCase()}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-foreground">{trade.qty}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-foreground">${trade.filledPrice?.toFixed(2) || trade.limitPrice?.toFixed(2) || 'N/A'}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${trade.status === 'filled'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : trade.status === 'failed'
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    }`}>
                                    {trade.status}
                                  </span>
                                </td>

                                <td className="py-3 px-4">
                                  <span className="text-sm text-muted">
                                    {trade.createdAt ? new Date(trade.createdAt).toLocaleDateString() : 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination for Trades */}
                      {tradesData.pagination && tradesData.pagination.pages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <div className="text-sm text-muted">
                            Showing {(tradesPage - 1) * itemsPerPage + 1} to {Math.min(tradesPage * itemsPerPage, tradesData.pagination.total)} of {tradesData.pagination.total} trades
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setTradesPage(p => Math.max(1, p - 1))}
                              disabled={tradesPage === 1}
                              className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiChevronLeft className="h-5 w-5" />
                            </button>
                            <span className="px-4 py-2 text-sm font-medium text-foreground">
                              Page {tradesPage} of {tradesData.pagination.pages}
                            </span>
                            <button
                              onClick={() => setTradesPage(p => Math.min(tradesData.pagination.pages, p + 1))}
                              disabled={tradesPage === tradesData.pagination.pages}
                              className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiChevronRight className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted">
                      <FiTrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium">No trades found</p>
                      <p className="text-sm mt-2">This user hasn't made any trades yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StatisticsPage;
